/**
 * Connector admin handler
 *
 * Manages the full connector hierarchy:
 * Account → Organization → Channels/Providers → Connections → Invitations/ThreadMappings
 */

import { useState, useCallback, useEffect } from "react";
import type { connectorTypes } from "@pineseed/spec";
import type { UseAdminAppReturn } from "./useAdminApp";
import type { AccountsAdminHandler } from "./account/useAccountsAdmin";
import type { OrganizationsAdminHandler } from "./account/useOrganizationsAdmin";

type Channel = connectorTypes["Channel"];
type Provider = connectorTypes["Provider"];
type Connection = connectorTypes["Connection"];
type Invitation = connectorTypes["Invitation"];
type ThreadMapping = connectorTypes["ThreadMapping"];

type AddressMapping = {
  address: string;
  channelId: string;
  workspaceId: string;
  createdAt: string;
};

type ThreadRoute = {
  externalThreadId: string;
  channelId: string;
  workspaceId: string;
  createdAt: string;
};

export interface ConnectorAdminHandler {
  // Selection state
  selectedAccountId: string;
  setSelectedAccountId: (id: string) => void;
  selectedOrgId: string;
  setSelectedOrgId: (id: string) => void;

  // Channels
  channels: Channel[];
  channelsLoading: boolean;
  channelsError: string | null;
  loadChannels: () => Promise<void>;
  createChannel: (data: { organizationId: string; name: string; type: string }) => Promise<Channel>;
  updateChannel: (id: string, data: { name?: string; type?: string }) => Promise<Channel>;
  deleteChannel: (id: string) => Promise<void>;

  // Providers
  providers: Provider[];
  providersLoading: boolean;
  providersError: string | null;
  loadProviders: () => Promise<void>;
  createProvider: (data: { organizationId: string; name: string; type: string }) => Promise<Provider>;
  updateProvider: (id: string, data: { name?: string; type?: string }) => Promise<Provider>;
  deleteProvider: (id: string) => Promise<void>;

  // Connections
  connections: Connection[];
  connectionsLoading: boolean;
  connectionsError: string | null;
  loadConnections: () => Promise<void>;
  createConnection: (data: { workspaceId: string; channelId: string; providerId: string; name: string }) => Promise<Connection>;
  updateConnection: (id: string, data: { name?: string }) => Promise<Connection>;
  deleteConnection: (id: string) => Promise<void>;

  // Invitations
  invitations: Invitation[];
  invitationsLoading: boolean;
  invitationsError: string | null;
  loadInvitations: (channelId: string) => Promise<void>;
  createInvitation: (data: {
    id: string;
    channelId: string;
    conversationId: string;
    fromParticipantId: string;
    expiresAt: string;
    createdByParticipantId: string;
    toExternalThreadId?: string;
  }) => Promise<Invitation>;
  revokeInvitation: (id: string) => Promise<void>;

  // Thread Mappings
  threadMappings: ThreadMapping[];
  threadMappingsLoading: boolean;
  threadMappingsError: string | null;
  loadThreadMappings: (channelId: string) => Promise<void>;
  createThreadMapping: (data: {
    channelId: string;
    internalThreadId: string;
    externalThreadId: string;
    externalParticipantId: string;
    fromParticipantId: string;
    createdByParticipantId: string;
  }) => Promise<void>;
  deleteThreadMapping: (externalThreadId: string, channelId: string) => Promise<void>;

  // Address Mappings
  addressMappings: AddressMapping[];
  addressMappingsLoading: boolean;
  addressMappingsError: string | null;
  loadAddressMappings: () => Promise<void>;
  createAddressMapping: (data: { address: string; channelId: string; workspaceId: string }) => Promise<void>;
  deleteAddressMapping: (address: string) => Promise<void>;

  // Thread Routes (read-only)
  threadRoutes: ThreadRoute[];
  threadRoutesLoading: boolean;
  threadRoutesError: string | null;
  loadThreadRoutes: () => Promise<void>;
}

export function useConnectorAdmin(
  adminApp: UseAdminAppReturn,
  _accounts: AccountsAdminHandler,
  organizations: OrganizationsAdminHandler,
): ConnectorAdminHandler {
  const { httpUrl } = adminApp;

  // Helper for authenticated fetch
  const apiFetch = useCallback(async (path: string, options?: RequestInit): Promise<Response> => {
    const wsId = adminApp.workspaceId;
    const response = await fetch(`${httpUrl}/api${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(wsId && { 'X-Workspace-Id': wsId }),
        ...(options?.headers || {}),
      },
      credentials: 'include',
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const msg = body?.error || response.statusText;
      throw new Error(msg);
    }
    return response;
  }, [httpUrl]);

  // ── Selection state ──────────────────────────────────────────────────────

  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  // When account changes, load its orgs and reset org selection
  useEffect(() => {
    if (selectedAccountId) {
      organizations.getOrganizationsByAccount(selectedAccountId);
      setSelectedOrgId('');
    }
  }, [selectedAccountId]);

  // ── Channels ─────────────────────────────────────────────────────────────

  const [channels, setChannels] = useState<Channel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelsError, setChannelsError] = useState<string | null>(null);

  const loadChannels = useCallback(async () => {
    if (!selectedOrgId) { setChannels([]); return; }
    setChannelsLoading(true);
    setChannelsError(null);
    try {
      const res = await apiFetch(`/organization/${selectedOrgId}/channels`);
      setChannels(await res.json());
    } catch (err) {
      setChannelsError(`Failed to load channels: ${err instanceof Error ? err.message : String(err)}`);
      setChannels([]);
    } finally {
      setChannelsLoading(false);
    }
  }, [apiFetch, selectedOrgId]);

  const createChannel = useCallback(async (data: { organizationId: string; name: string; type: string }): Promise<Channel> => {
    const res = await apiFetch('/channel', { method: 'POST', body: JSON.stringify(data) });
    const created = await res.json();
    await loadChannels();
    return created;
  }, [apiFetch, loadChannels]);

  const updateChannel = useCallback(async (id: string, data: { name?: string; type?: string }): Promise<Channel> => {
    const res = await apiFetch(`/channel?id=${id}`, { method: 'PUT', body: JSON.stringify(data) });
    const updated = await res.json();
    await loadChannels();
    return updated;
  }, [apiFetch, loadChannels]);

  const deleteChannel = useCallback(async (id: string): Promise<void> => {
    await apiFetch(`/channel?id=${id}`, { method: 'DELETE' });
    await loadChannels();
  }, [apiFetch, loadChannels]);

  // Reload channels when org changes
  useEffect(() => { loadChannels(); }, [selectedOrgId]);

  // ── Providers ────────────────────────────────────────────────────────────

  const [providers, setProviders] = useState<Provider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [providersError, setProvidersError] = useState<string | null>(null);

  const loadProviders = useCallback(async () => {
    if (!selectedOrgId) { setProviders([]); return; }
    setProvidersLoading(true);
    setProvidersError(null);
    try {
      const res = await apiFetch(`/organization/${selectedOrgId}/providers`);
      setProviders(await res.json());
    } catch (err) {
      setProvidersError(`Failed to load providers: ${err instanceof Error ? err.message : String(err)}`);
      setProviders([]);
    } finally {
      setProvidersLoading(false);
    }
  }, [apiFetch, selectedOrgId]);

  const createProvider = useCallback(async (data: { organizationId: string; name: string; type: string }): Promise<Provider> => {
    const res = await apiFetch('/provider', { method: 'POST', body: JSON.stringify(data) });
    const created = await res.json();
    await loadProviders();
    return created;
  }, [apiFetch, loadProviders]);

  const updateProvider = useCallback(async (id: string, data: { name?: string; type?: string }): Promise<Provider> => {
    const res = await apiFetch(`/provider?id=${id}`, { method: 'PUT', body: JSON.stringify(data) });
    const updated = await res.json();
    await loadProviders();
    return updated;
  }, [apiFetch, loadProviders]);

  const deleteProvider = useCallback(async (id: string): Promise<void> => {
    await apiFetch(`/provider?id=${id}`, { method: 'DELETE' });
    await loadProviders();
  }, [apiFetch, loadProviders]);

  // Reload providers when org changes
  useEffect(() => { loadProviders(); }, [selectedOrgId]);

  // ── Connections ──────────────────────────────────────────────────────────

  const [connections, setConnections] = useState<Connection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionsError, setConnectionsError] = useState<string | null>(null);

  const loadConnections = useCallback(async () => {
    const wsId = adminApp.workspaceId;
    if (!wsId) { setConnections([]); return; }
    setConnectionsLoading(true);
    setConnectionsError(null);
    try {
      const res = await apiFetch(`/workspace/${wsId}/connections`);
      setConnections(await res.json());
    } catch (err) {
      setConnectionsError(`Failed to load connections: ${err instanceof Error ? err.message : String(err)}`);
      setConnections([]);
    } finally {
      setConnectionsLoading(false);
    }
  }, [apiFetch, adminApp.workspaceId]);

  const createConnection = useCallback(async (data: { workspaceId: string; channelId: string; providerId: string; name: string }): Promise<Connection> => {
    const res = await apiFetch('/connections', { method: 'POST', body: JSON.stringify(data) });
    const created = await res.json();
    await loadConnections();
    return created;
  }, [apiFetch, loadConnections]);

  const updateConnection = useCallback(async (id: string, data: { name?: string }): Promise<Connection> => {
    const res = await apiFetch(`/connections?id=${id}`, { method: 'PUT', body: JSON.stringify(data) });
    const updated = await res.json();
    await loadConnections();
    return updated;
  }, [apiFetch, loadConnections]);

  const deleteConnection = useCallback(async (id: string): Promise<void> => {
    await apiFetch(`/connections?id=${id}`, { method: 'DELETE' });
    await loadConnections();
  }, [apiFetch, loadConnections]);

  // Reload connections when workspace changes
  useEffect(() => { loadConnections(); }, [adminApp.workspaceId]);

  // ── Invitations ──────────────────────────────────────────────────────────

  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [invitationsError, setInvitationsError] = useState<string | null>(null);

  const loadInvitations = useCallback(async (channelId: string) => {
    setInvitationsLoading(true);
    setInvitationsError(null);
    try {
      const res = await apiFetch(`/channel/${channelId}/invitations`);
      setInvitations(await res.json());
    } catch (err) {
      setInvitationsError(`Failed to load invitations: ${err instanceof Error ? err.message : String(err)}`);
      setInvitations([]);
    } finally {
      setInvitationsLoading(false);
    }
  }, [apiFetch]);

  const createInvitation = useCallback(async (data: {
    id: string;
    channelId: string;
    conversationId: string;
    fromParticipantId: string;
    expiresAt: string;
    createdByParticipantId: string;
    toExternalThreadId?: string;
  }): Promise<Invitation> => {
    const res = await apiFetch('/invitation', { method: 'POST', body: JSON.stringify(data) });
    const created = await res.json();
    await loadInvitations(data.channelId);
    return created;
  }, [apiFetch, loadInvitations]);

  const revokeInvitation = useCallback(async (id: string): Promise<void> => {
    await apiFetch(`/invitation/revoke?id=${id}`, { method: 'POST' });
    // Reload if we have a channel context
    if (channels.length > 0 && invitations.length > 0) {
      const inv = invitations.find(i => i.id === id);
      if (inv) await loadInvitations(inv.channelId);
    }
  }, [apiFetch, loadInvitations, channels, invitations]);

  // ── Thread Mappings ──────────────────────────────────────────────────────

  const [threadMappings, setThreadMappings] = useState<ThreadMapping[]>([]);
  const [threadMappingsLoading, setThreadMappingsLoading] = useState(false);
  const [threadMappingsError, setThreadMappingsError] = useState<string | null>(null);

  const loadThreadMappings = useCallback(async (channelId: string) => {
    setThreadMappingsLoading(true);
    setThreadMappingsError(null);
    try {
      const res = await apiFetch(`/channel/${channelId}/thread-mappings`);
      setThreadMappings(await res.json());
    } catch (err) {
      setThreadMappingsError(`Failed to load thread mappings: ${err instanceof Error ? err.message : String(err)}`);
      setThreadMappings([]);
    } finally {
      setThreadMappingsLoading(false);
    }
  }, [apiFetch]);

  const createThreadMapping = useCallback(async (data: {
    channelId: string;
    internalThreadId: string;
    externalThreadId: string;
    externalParticipantId: string;
    fromParticipantId: string;
    createdByParticipantId: string;
  }): Promise<void> => {
    await apiFetch('/invitation/create-and-accept', { method: 'POST', body: JSON.stringify(data) });
    await loadThreadMappings(data.channelId);
  }, [apiFetch, loadThreadMappings]);

  const deleteThreadMapping = useCallback(async (externalThreadId: string, channelId: string): Promise<void> => {
    await apiFetch(`/thread-mapping?externalThreadId=${encodeURIComponent(externalThreadId)}&channelId=${channelId}`, { method: 'DELETE' });
    await loadThreadMappings(channelId);
  }, [apiFetch, loadThreadMappings]);

  // ── Address Mappings ─────────────────────────────────────────────────────

  const [addressMappings, setAddressMappings] = useState<AddressMapping[]>([]);
  const [addressMappingsLoading, setAddressMappingsLoading] = useState(false);
  const [addressMappingsError, setAddressMappingsError] = useState<string | null>(null);

  const loadAddressMappings = useCallback(async () => {
    setAddressMappingsLoading(true);
    setAddressMappingsError(null);
    try {
      const res = await apiFetch('/address-mappings');
      setAddressMappings(await res.json());
    } catch (err) {
      setAddressMappingsError(`Failed to load address mappings: ${err instanceof Error ? err.message : String(err)}`);
      setAddressMappings([]);
    } finally {
      setAddressMappingsLoading(false);
    }
  }, [apiFetch]);

  const createAddressMapping = useCallback(async (data: { address: string; channelId: string; workspaceId: string }): Promise<void> => {
    await apiFetch('/address-mapping', { method: 'POST', body: JSON.stringify(data) });
    await loadAddressMappings();
  }, [apiFetch, loadAddressMappings]);

  const deleteAddressMapping = useCallback(async (address: string): Promise<void> => {
    await apiFetch(`/address-mapping?address=${encodeURIComponent(address)}`, { method: 'DELETE' });
    await loadAddressMappings();
  }, [apiFetch, loadAddressMappings]);

  // Auto-load address mappings on mount
  useEffect(() => { loadAddressMappings(); }, []);

  // ── Thread Routes (read-only) ────────────────────────────────────────────

  const [threadRoutes, setThreadRoutes] = useState<ThreadRoute[]>([]);
  const [threadRoutesLoading, setThreadRoutesLoading] = useState(false);
  const [threadRoutesError, setThreadRoutesError] = useState<string | null>(null);

  const loadThreadRoutes = useCallback(async () => {
    setThreadRoutesLoading(true);
    setThreadRoutesError(null);
    try {
      const res = await apiFetch('/thread-routes');
      setThreadRoutes(await res.json());
    } catch (err) {
      setThreadRoutesError(`Failed to load thread routes: ${err instanceof Error ? err.message : String(err)}`);
      setThreadRoutes([]);
    } finally {
      setThreadRoutesLoading(false);
    }
  }, [apiFetch]);

  // Auto-load thread routes on mount
  useEffect(() => { loadThreadRoutes(); }, []);

  return {
    selectedAccountId, setSelectedAccountId,
    selectedOrgId, setSelectedOrgId,

    channels, channelsLoading, channelsError,
    loadChannels, createChannel, updateChannel, deleteChannel,

    providers, providersLoading, providersError,
    loadProviders, createProvider, updateProvider, deleteProvider,

    connections, connectionsLoading, connectionsError,
    loadConnections, createConnection, updateConnection, deleteConnection,

    invitations, invitationsLoading, invitationsError,
    loadInvitations, createInvitation, revokeInvitation,

    threadMappings, threadMappingsLoading, threadMappingsError,
    loadThreadMappings, createThreadMapping, deleteThreadMapping,

    addressMappings, addressMappingsLoading, addressMappingsError,
    loadAddressMappings, createAddressMapping, deleteAddressMapping,

    threadRoutes, threadRoutesLoading, threadRoutesError,
    loadThreadRoutes,
  };
}
